import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useSetConnectorGeoFilter,
  type AdminConnectorGeoFilter,
  type AdminGeoFilter,
} from "@/features/admin";
import { defaultGeoFilter } from "@/features/connectors/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GeoFilterField } from "../../../backoffice/connectors/components/geo-filter-field";

/**
 * Which countries may call this connector.
 *
 * Reuses the tenant-facing field rather than a second copy of the country
 * picker: the tenant sees it read-only on their own connector page, staff see
 * the same control here writable, and there is one place where "what does
 * allow_unknown mean" is explained.
 */
export const GeoFilterDialog = ({
  connector,
  onClose,
}: {
  connector: AdminConnectorGeoFilter | null;
  onClose: () => void;
}) => {
  const setFilter = useSetConnectorGeoFilter();
  const [filter, setLocalFilter] = useState<AdminGeoFilter>(defaultGeoFilter());

  useEffect(() => {
    if (!connector) return;
    setLocalFilter(connector.geo_filter || defaultGeoFilter());
  }, [connector]);

  const save = async () => {
    if (!connector) return;
    try {
      await setFilter.mutateAsync({
        clientId: connector.client_id,
        connectorId: connector.id,
        geoFilter: filter,
      });
      toast.success("Filtre enregistré");
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Enregistrement impossible",
      );
    }
  };

  return (
    <Dialog
      open={Boolean(connector)}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {connector?.client_name} —{" "}
            {(connector?.phone_numbers || []).join(", ") || connector?.id}
          </DialogTitle>
          <DialogDescription>
            Les pays depuis lesquels ce connecteur accepte les appels. Chaque
            appel qui passe nous coûte une jambe opérateur et un pipeline
            complet.
          </DialogDescription>
        </DialogHeader>

        <GeoFilterField value={filter} onChange={setLocalFilter} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={save}
            disabled={
              setFilter.isPending ||
              (filter.enabled && filter.countries.length === 0)
            }
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
